import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { IFilterParams } from 'ag-grid-community'
import { ASSET_SET_FILTER, AssetInventoryType, AssetLookup, AssetSetFilterModel, MAX_FILTER_VALUES } from 'models'
import { loadAssetLookup } from '../utils/assetLookup'
import { FilterPanel } from './assetSetFilter.styles'

/* A column can hold hundreds of thousands of values; the rendered list is capped, the search is not. */
const MAX_RENDERED = 10000

export interface AssetSetFilterParams extends IFilterParams {
  assetType: AssetInventoryType
  columnName: string
  /* For columns without contains_: Select All over a term goes out as the values it matched. */
  enumerate?: boolean
  formatValue?: (value: string) => string
  /* Set by the grid's reactive filter wrapper; undefined when rendered outside a grid. */
  model?: AssetSetFilterModel | null
}

const EMPTY_LOOKUP: AssetLookup = { available: true, values: [], hasNoValue: false, complete: false }

const emptyModel = (): AssetSetFilterModel => ({
  filterType: ASSET_SET_FILTER,
  searchTerm: '',
  selectAll: false,
  checked: [],
  unchecked: [],
  noValue: false
})

/* Mirrors what contains_ compiles to server-side, so Select All applies what is on screen. */
const matches = (value: string, term: string) => value.toLowerCase().includes(term.toLowerCase())

/* Select All counts as applied even with no term, so it gets a chip like the Off-SLA grid's filters. */
const isMeaningful = (model: AssetSetFilterModel): boolean =>
  model.selectAll || model.checked.length > 0 || model.noValue

/* Chip text has to say what was applied: a term reads nothing like the values it matched. */
export const describeAssetSetFilter = (
  model: AssetSetFilterModel,
  formatValue: (value: string) => string = String
): string => {
  const term = model.searchTerm.trim()

  if (model.selectAll) {
    if (!term) {
      const all = model.unchecked.length ? `All (-${model.unchecked.length})` : 'All'

      return model.noValue ? all : `${all}, excluding (No value)`
    }

    const text = model.unchecked.length ? `contains "${term}" (-${model.unchecked.length})` : `contains "${term}"`

    return model.noValue ? `${text}, (No value)` : text
  }

  const checked = model.checked.map(formatValue)

  return (model.noValue ? [...checked, '(No value)'] : checked).join(', ')
}

export const AssetSetFilter = forwardRef<any, AssetSetFilterParams>((props, ref) => {
  const { assetType, columnName, enumerate = false, formatValue = String, api, column, model } = props

  const [lookup, setLookup] = useState<AssetLookup>(EMPTY_LOOKUP)
  const [term, setTerm] = useState('')
  const [selectAll, setSelectAll] = useState(false)
  const [checked, setChecked] = useState<string[]>([])
  const [unchecked, setUnchecked] = useState<string[]>([])
  const [noValue, setNoValue] = useState(false)

  /* AG Grid reads getModel straight after setModel, before React has re-rendered. */
  const appliedRef = useRef<AssetSetFilterModel>(emptyModel())

  useEffect(() => loadAssetLookup(assetType, columnName, setLookup), [assetType, columnName])

  const restore = useCallback(() => {
    const applied = appliedRef.current

    setTerm(applied.searchTerm)
    setSelectAll(applied.selectAll)
    setChecked(applied.checked)
    setUnchecked(applied.unchecked)
    setNoValue(applied.noValue)
  }, [])

  /* Reactive grids never call setModel, so a model set from outside, like Reset all, arrives only as this prop. */
  useEffect(() => {
    if (model === undefined) return

    appliedRef.current = model ? { ...emptyModel(), ...model } : emptyModel()
    restore()
  }, [model, restore])

  useImperativeHandle(
    ref,
    () => ({
      /* Rows are filtered by the server, so the grid never asks a row to pass this filter. */
      doesFilterPass: () => true,
      isFilterActive: () => isMeaningful(appliedRef.current),
      getModel: () => (isMeaningful(appliedRef.current) ? appliedRef.current : null),
      setModel: (model: AssetSetFilterModel | null) => {
        appliedRef.current = model ? { ...emptyModel(), ...model } : emptyModel()
        restore()
      },
      afterGuiAttached: restore,
      getModelAsString: () => describeAssetSetFilter(appliedRef.current, formatValue)
    }),
    [restore, formatValue]
  )

  const visible = useMemo(() => {
    const trimmed = term.trim()
    const source = trimmed
      ? lookup.values.filter((value) => matches(value, trimmed) || matches(formatValue(value), trimmed))
      : lookup.values

    return { all: source, rows: source.slice(0, MAX_RENDERED), total: source.length }
  }, [lookup.values, term, formatValue])

  const isRowChecked = (value: string) => (selectAll ? !unchecked.includes(value) : checked.includes(value))

  const toggleRow = (value: string) => {
    const update = (current: string[]) =>
      current.includes(value) ? current.filter((item) => item !== value) : [...current, value]

    if (selectAll) setUnchecked(update)
    else setChecked(update)
  }

  /* (No value) follows Select All when nothing is typed; a term narrows to matches, which empty cells never are. */
  const toggleSelectAll = () => {
    const next = !selectAll

    setSelectAll(next)
    setChecked([])
    setUnchecked([])
    setNoValue(next && !term.trim())
  }

  /* Exclusions belong to the term they were made under, or they ride along invisibly. */
  const changeTerm = (next: string) => {
    setTerm(next)
    setUnchecked([])
    if (selectAll) setNoValue(!next.trim())
  }

  const selectedCount = !selectAll
    ? checked.length
    : enumerate && (term.trim() || unchecked.length)
    ? visible.all.filter((value) => !unchecked.includes(value)).length
    : unchecked.length
  const overLimit = selectedCount > MAX_FILTER_VALUES

  const closePopup = () =>
    document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', keyCode: 27, bubbles: true }))

  const commit = (model: AssetSetFilterModel | null) => {
    appliedRef.current = model ?? emptyModel()

    const columnId = column.getColId()
    const next = { ...(api.getFilterModel() || {}) }

    if (model && isMeaningful(model)) next[columnId] = model
    else delete next[columnId]

    api.setFilterModel(next)
  }

  const handleApply = () => {
    if (overLimit) return

    if (enumerate && selectAll && (term.trim() || unchecked.length)) {
      const matched = visible.all.filter((value) => !unchecked.includes(value))

      commit({ ...emptyModel(), checked: matched, noValue })
    } else {
      commit({ filterType: ASSET_SET_FILTER, searchTerm: term, selectAll, checked, unchecked, noValue })
    }

    closePopup()
  }

  /* Closed before the grid refetches, as Apply does, so the open panel does not redraw with it. */
  const handleReset = () => {
    setTerm('')
    setSelectAll(false)
    setChecked([])
    setUnchecked([])
    setNoValue(false)
    closePopup()
    commit(null)
  }

  const allTicked = selectAll && !unchecked.length
  const headerName = column.getColDef().headerName ?? columnName

  return (
    <FilterPanel>
      <Box className="search-wrap">
        <input
          className="search-field"
          value={term}
          placeholder="Search..."
          aria-label={`Search ${headerName} values`}
          onChange={(event) => changeTerm(event.target.value)}
        />
      </Box>

      <Box className="value-list">
        <Box
          component="button"
          type="button"
          className="value-row"
          role="checkbox"
          aria-checked={allTicked ? 'true' : selectAll ? 'mixed' : 'false'}
          onClick={toggleSelectAll}
        >
          <Box
            component="span"
            className={`value-box ${allTicked ? 'is-checked' : selectAll ? 'is-indeterminate' : ''}`}
          />
          <Box component="span" className="value-text">
            (Select All)
          </Box>
        </Box>

        {lookup.hasNoValue && !enumerate && (
          <Box
            component="button"
            type="button"
            className="value-row is-muted"
            role="checkbox"
            aria-checked={noValue}
            onClick={() => setNoValue((current) => !current)}
          >
            <Box component="span" className={`value-box ${noValue ? 'is-checked' : ''}`} />
            <Box component="span" className="value-text">
              (No value)
            </Box>
          </Box>
        )}

        {visible.rows.map((value) => (
          <Box
            component="button"
            type="button"
            key={value}
            className="value-row"
            role="checkbox"
            aria-checked={isRowChecked(value)}
            onClick={() => toggleRow(value)}
          >
            <Box component="span" className={`value-box ${isRowChecked(value) ? 'is-checked' : ''}`} />
            <Box component="span" className="value-text" title={value}>
              {formatValue(value)}
            </Box>
          </Box>
        ))}
      </Box>

      {!lookup.available && <Typography className="panel-note">No values to filter on yet.</Typography>}

      {visible.total > MAX_RENDERED && (
        <Typography className="panel-note">
          Showing {MAX_RENDERED.toLocaleString()} of {visible.total.toLocaleString()} — narrow the search, or use Select
          All to filter on the term itself.
        </Typography>
      )}

      {lookup.available && !lookup.complete && <Typography className="panel-note">Loading…</Typography>}

      {overLimit && (
        <Typography className="panel-note is-warning">
          {selectedCount} selected, and {MAX_FILTER_VALUES} is the limit. Search for what they have in common and use
          Select All instead.
        </Typography>
      )}

      <Box className="panel-buttons">
        <Box component="button" type="button" onClick={handleApply} disabled={overLimit}>
          Apply
        </Box>
        <Box component="button" type="button" onClick={handleReset}>
          Reset
        </Box>
      </Box>
    </FilterPanel>
  )
})
